import { clsx } from 'clsx'
import { Activity } from 'lucide-react'
import { formatUptime } from '@/lib/format'
import type { EngineHealth } from '@/lib/types'

const QUEUE_DEPTH_GREEN = 100
const QUEUE_DEPTH_YELLOW = 1000

function queueDepthColor(depth: number): string {
  if (depth < QUEUE_DEPTH_GREEN) return 'text-green-600'
  if (depth < QUEUE_DEPTH_YELLOW) return 'text-yellow-600'
  return 'text-red-600'
}

interface HealthPanelProps {
  health: EngineHealth | undefined
  isLoading: boolean
}

export function HealthPanel({ health, isLoading }: HealthPanelProps): React.JSX.Element {
  if (isLoading || !health) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <div className="flex items-center gap-2 text-text-secondary">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
          <span className="text-sm">Connecting to engine...</span>
        </div>
      </div>
    )
  }

  const isHealthy = health.status === 'healthy'

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex flex-wrap items-center gap-6">
        {/* Status indicator */}
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'inline-block h-3.5 w-3.5 rounded-full',
              isHealthy ? 'bg-green-500' : 'bg-yellow-500',
            )}
          />
          <span className="text-lg font-semibold text-text-primary">
            {isHealthy ? 'Healthy' : 'Degraded'}
          </span>
        </div>

        {/* Divider */}
        <div className="hidden h-8 w-px bg-surface-3 sm:block" />

        {/* Version */}
        <div>
          <p className="text-xs text-text-tertiary">Version</p>
          <p className="text-sm font-medium text-text-primary">{health.version}</p>
        </div>

        {/* Divider */}
        <div className="hidden h-8 w-px bg-surface-3 sm:block" />

        {/* Uptime */}
        <div>
          <p className="text-xs text-text-tertiary">Uptime</p>
          <p className="text-sm font-medium text-text-primary">
            {formatUptime(health.uptime_seconds)}
          </p>
        </div>

        {/* Divider */}
        <div className="hidden h-8 w-px bg-surface-3 sm:block" />

        {/* Queue depth */}
        <div>
          <p className="text-xs text-text-tertiary">Queue Depth</p>
          <p className={clsx('text-2xl font-bold', queueDepthColor(health.queue_depth))}>
            {health.queue_depth.toLocaleString()}
          </p>
        </div>

        {/* Live indicator */}
        <div className="ml-auto flex items-center gap-2">
          <Activity className="h-4 w-4 text-green-500" />
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
          </span>
          <span className="text-xs font-medium text-green-600">Live</span>
        </div>
      </div>
    </div>
  )
}
